import React, { useEffect } from "react";
import { MapContainer, TileLayer, Marker, Popup, Circle, useMap } from "react-leaflet";
import L from "leaflet";

// Fix default marker icons (Leaflet asset issue with bundlers)
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
});

// Distinct icon for the current user's own location.
const userIcon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41], iconAnchor: [12, 41], popupAnchor: [1, -34],
  className: "map-user-pin",
});

function FitBounds({ points }) {
  const map = useMap();
  useEffect(() => {
    if (!points || points.length === 0) return;
    if (points.length === 1) {
      map.setView(points[0], 12, { animate: true });
      return;
    }
    const bounds = L.latLngBounds(points);
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 13 });
  }, [JSON.stringify(points)]); // eslint-disable-line react-hooks/exhaustive-deps -- deep-compare via stringified points (raw array identity changes every render)
  return null;
}

export default function MapLeaflet({
  markers = [],
  circles = [],
  userLocation = null,
  center = [51.5, -0.12],
  zoom = 6,
  height = 320,
  fitBounds = false,
  testId = "map-container",
}) {
  const boundsPts = fitBounds
    ? [
        ...markers.filter(m => m.lat != null && m.lng != null).map(m => [m.lat, m.lng]),
        ...(userLocation ? [[userLocation.lat, userLocation.lng]] : []),
      ]
    : [];

  return (
    <div style={{ height }} className="rounded-xl overflow-hidden border border-neutral-200" data-testid={testId}>
      <MapContainer center={center} zoom={zoom} scrollWheelZoom={false} style={{ height: "100%", width: "100%" }}>
        <TileLayer
          attribution='&copy; OpenStreetMap'
          url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
        />
        {circles.map((c, i) =>
          c.lat != null && c.lng != null ? (
            <Circle
              key={`circle-${i}`}
              center={[c.lat, c.lng]}
              radius={(c.radius_miles || 5) * 1609}
              pathOptions={{
                color: c.color || "#1A4331",
                fillColor: c.color || "#1A4331",
                fillOpacity: c.fillOpacity ?? 0.08,
                weight: 1.5,
              }}
            />
          ) : null
        )}
        {markers.map((m, i) =>
          m.lat != null && m.lng != null ? (
            <Marker key={`marker-${i}`} position={[m.lat, m.lng]}>
              <Popup>
                <div className="font-medium">{m.title}</div>
                {m.subtitle && <div className="text-xs text-neutral-500">{m.subtitle}</div>}
                {m.link && (
                  <a href={m.link} className="text-xs font-medium text-forest hover:underline mt-1 inline-block">
                    Open →
                  </a>
                )}
              </Popup>
            </Marker>
          ) : null
        )}
        {userLocation && userLocation.lat != null && userLocation.lng != null && (
          <Marker position={[userLocation.lat, userLocation.lng]} icon={userIcon}>
            <Popup><div className="text-xs">You are here</div></Popup>
          </Marker>
        )}
        {fitBounds && boundsPts.length > 0 && <FitBounds points={boundsPts} />}
      </MapContainer>
    </div>
  );
}
